import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { Grid, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { yupResolver } from '@hookform/resolvers/yup';

import {
  Button, FormActionWrapper, FormAutocomplete, FormTextField,
} from '~common/components';
import { dataLists, validationSchemas } from '~common/utils';
import SubjectsField from '~common/components/form/formSubjectsField/index.jsx';
import useStyles from './styles';
import BackButton from '../backButton';
import { formatFieldName } from '../helpers';

const {
  schoolOptions,
  academicTypeOptions,
  academicValuesOptions,
} = dataLists;

const LearnerInfoPanel = ({
  formData,
  onNextStep,
  submitOnPreviousStep,
  onAddAnotherLearner,
  learnerIdxRef,
  isLastLearner,
}) => {
  const { classes } = useStyles();
  const {
    handleSubmit, control, getValues, setValue, trigger, formState: { errors },
  } = useForm(
    { defaultValues: formData, resolver: yupResolver(validationSchemas.learnerInfoPanelSchema) },
  );

  return (
    <form onSubmit={handleSubmit(onNextStep)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormAutocomplete
            name={formatFieldName('currentSchool', learnerIdxRef)}
            control={control}
            options={schoolOptions}
            getOptionLabel={(option) => {
              if (option === undefined) {
                return '';
              }
              return option;
            }}
            label="What school do they currently attend?"
            defaultValue={[getValues(formatFieldName('currentSchool', learnerIdxRef))]}
            inputProps={{
              label: 'Please Choose *',
              type: 'search',
            }}
            freeSolo
            handleHomeEndKeys
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" className={classes.label}>
            What Academic Year are they currently in?
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <FormAutocomplete
            name={formatFieldName('academicType', learnerIdxRef)}
            control={control}
            options={academicTypeOptions}
            getOptionLabel={(option) => option.value}
            defaultValue={getValues(formatFieldName('academicType', learnerIdxRef))}
            inputProps={{
              label: 'Please Choose *',
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <FormAutocomplete
            name={formatFieldName('academicValue', learnerIdxRef)}
            control={control}
            options={academicValuesOptions}
            getOptionLabel={(option) => option.value}
            defaultValue={getValues(formatFieldName('academicValue', learnerIdxRef))}
            inputProps={{
              label: 'Please Choose *',
            }}
          />
        </Grid>
      </Grid>

      <SubjectsField
        control={control}
        getValues={getValues}
        setValue={setValue}
        trigger={trigger}
        overwriteName={formatFieldName('subjectList', learnerIdxRef)}
        errors={errors}
      />

      <Grid container columnSpacing={3}>
        <Grid item xs={12}>
          <FormTextField
            name={formatFieldName('aboutYou', learnerIdxRef)}
            control={control}
            label="Any Additional information"
            inputProps={{
              multiline: true,
              rows: 7,
              margin: 'dense',
              placeholder: `Please tell us more about the learner...
      - Favourite subjects
      - Extra-curricular activities
      - Educational aspirations`,
            }}
          />
        </Grid>
      </Grid>

      {
        isLastLearner && (
          <Grid container columnSpacing={3} className={classes.addLearnerGrid}>
            <Grid item xs={12}>
              <Button onClick={handleSubmit(onAddAnotherLearner)} variant="outlined" endIcon={<AddIcon />}>
                Add another learner
              </Button>
            </Grid>
          </Grid>
        )
      }
      <FormActionWrapper>
        <BackButton onClick={submitOnPreviousStep} />
        {
          isLastLearner
          && (
            <Button onClick={handleSubmit(onAddAnotherLearner)} variant="outlined" className={classes.addLearnerButton} endIcon={<AddIcon />}>
              Add another learner
            </Button>
          )
        }
        <Button type="submit">Next</Button>
      </FormActionWrapper>
    </form>
  );
};

LearnerInfoPanel.propTypes = {
  formData: PropTypes.shape({}).isRequired,
  onNextStep: PropTypes.func.isRequired,
  submitOnPreviousStep: PropTypes.func.isRequired,
  onAddAnotherLearner: PropTypes.func.isRequired,
  learnerIdxRef: PropTypes.shape({
    current: PropTypes.number.isRequired,
  }).isRequired,
  isLastLearner: PropTypes.bool.isRequired,
};

export default LearnerInfoPanel;
